// Mapped Objects
export enum AgentStudioWizardMode {
  CREATE = 'create',
  EDIT = 'edit',
}
export type TAgentStudioWizardMode = AgentStudioWizardMode.CREATE | AgentStudioWizardMode.EDIT;

export enum AgentStudioTypePageMap {
  TEMPLATE = 'template',
  COMPONENT = 'library'
}
export type TAgentStudioTypePageMap = AgentStudioTypePageMap.TEMPLATE | AgentStudioTypePageMap.COMPONENT;

export enum TemplateRowOptions {
  EDIT_TEMPLATE = 'edit',
  DELETE_TEMPLATE = 'delete',
}

export enum ComponentType {
  PRODUCT_QA = 'product-qa',
  CONVERSATIONAL_QA_FAQ = 'conversational-qa-faq',
  CONVERSATIONAL_QA = 'conversational-qa',
  DOCUMENT_SUMMARIES = 'document-summaries',
  FITMENT_SPECIALIST = 'fitment-specialist',
  GENERATIVE_ANSWERS = 'generative-answers',
  QUESTIONS_AND_ANSWERS = 'questions-and-answers',
  REVIEW_SUMMARIES = 'review-summaries',
  SHOPPING_ASSISTANT = 'shopping-assistant',
  TECH_SPEC_INTERROGATION = 'tech-spec-interrogation',
}

export const agentTemplateTypes = Object.keys(ComponentType);

export type TComponentType = ComponentType;

export const ComponentDisplayNames = {
  [ComponentType.PRODUCT_QA]: 'Product Q&A',
  [ComponentType.CONVERSATIONAL_QA_FAQ]: 'Conversational Q&A with FAQ',
  [ComponentType.CONVERSATIONAL_QA]: 'Conversational Q&A',
  [ComponentType.DOCUMENT_SUMMARIES]: 'Document Summaries',
  [ComponentType.FITMENT_SPECIALIST]: 'Fitment Specialist',
  [ComponentType.GENERATIVE_ANSWERS]: 'Generative Answers',
  [ComponentType.QUESTIONS_AND_ANSWERS]: 'Questions & Answers',
  [ComponentType.REVIEW_SUMMARIES]: 'Review Summaries',
  [ComponentType.SHOPPING_ASSISTANT]: 'Shopping/Research Assistant',
  [ComponentType.TECH_SPEC_INTERROGATION]: 'Tech Spec Interrogation',
};

// Agent Studio Components
export interface AgentStudioComponent {
  name: string;
  description: string;
  type: ComponentType;
  category: string;
  enabled: boolean;
  datastoreCompatibilityType: string;
  cssOverrides?: string;
  templateType?: string;
  supportedComponentType?: string;
  supportedCardType?: string;
  filterParameter?: string;
  disabled?: boolean;
}

// Agent Studio Solution Packs
export interface SolutionPackModelValue {
  value: string;
  name: string;
  modelProvider?: string;
  recommended?: boolean;
}

export interface SolutionPackParameter {
  parameter: string;
  type: string;
  name: string;
  allowedValues?: SolutionPackModelValue[];
}

export interface SolutionPackUseCase {
  useCase: string;
  name: string;
  indexParameters?: SolutionPackParameter[];
  queryParameters?: SolutionPackParameter[];
}


export interface SolutionPack {
  solutionPack: string;
  name: string;
  parameters: SolutionPackParameter[];
  useCases: SolutionPackUseCase[];
  preview?: any;
}

export interface SolutionPackDetails {
  solutionPack: string;
  name: string;
  domain?: SolutionPackParameter;
  useCases: SolutionPackUseCase[];
  preview?: any;
}

// Agent Studio Templates
export interface IndexTarget {
  id?: string;
  name: string;
  region: string;
  solutionPackType: string; // Maps to component templateType
  domain?: string;
  cells?: string;
  createdBy?: string;
  creationDate?: string;
  lastModifiedBy?: string;
  lastModified?: string;
  state?: string;
}

export type IndexTargetWithModelIds = IndexTarget & {
  [key: string]: unknown
}

export interface AgentConfiguration {
  id: string;
  agentType: ComponentType.CONVERSATIONAL_QA | ComponentType.CONVERSATIONAL_QA_FAQ;
  createdBy: string;
  creationDate: string;
  lastModifiedBy?: string;
  lastModified?: string;
  name: string;
  indexTargetId: string;
  conversationModelId: string;
  domain: string;
}

export interface TemplateDatastore {
  id?: string;
  name: string;
  type?: 'QA_PAIR';
  indexTargetId: string;
}

export interface TemplateDatastoreQuery {
  id: string;
  name: string;
  datastoreId: string;
}

export interface TemplateComponent {
  type: string;
  id?: string;
  name: string;
  datastoreQueryId?: string;
  agentConfigurationId?: string
}

export enum TemplateDataIngestionState {
  LOADING = 'Loading',
  DATA_LOADED = 'Ready',
  NO_DATA = 'No data loaded',
  ERROR_LOADING = 'Error loading'
}

export interface AgentStudioTemplate {
  id?: string;
  initialId: string;
  versionNumber?: number;
  createdBy?: string;
  creationDate?: string;
  lastModifiedBy?: string;
  lastModified?: string;
  type: string;
  name: string;
  layout?: Record<string, any>;
  extraction?: Extraction;
  datastoreQueries?: TemplateDatastoreQuery[];
  components: TemplateComponent[];
  state?: TemplateDataIngestionState;
}

export interface AgentStudioTemplateRequest {
  name: string;
  type: string;
  layout?: Record<string, any>;
  extraction?: Extraction;
  datastoreQueries?: TemplateDatastoreQuery[];
  components: TemplateComponent[];
}

export type AgentStudioTemplateDetails = Pick<AgentStudioTemplate,
  'id' | 'name' | 'initialId' | 'versionNumber' | 'extraction' | 'datastoreQueries' | 'components'>
  & Pick<AgentStudioComponent, 'type'>
  & Pick<IndexTargetWithModelIds,  'domain'>
  & { indexTargetId?: IndexTarget['id'], [key: string]: unknown }; //for unknown ids

// Agent Studio Data sources
export enum DataSourceTypes {
  GCS_PRODUCT_CATALOG = "Google Cloud Storage"
}
type TDataSourceTypes = keyof typeof DataSourceTypes;

export interface AgentStudioDataSourceLink {
  solutionId?: string;
  indexTargetId?: string;
  labels?: string[];
}

export interface GCSServiceAccountCredentials {
  type: 'GCS_SA';
  jsonKey: string;
}

export interface AgentStudioDataSourceRequest {
  name: string;
  type: TDataSourceTypes;
  region: string;
  credentials: GCSServiceAccountCredentials;
  tags?: string[];
  crawlSchedule?: {
    description?: string;
    hour?: number;
    interval?: number;
    daysOfWeek?: number[];
    daysOfMonth?: number[];
  };
  bucketName: string;
  triggerRun?: boolean;
  links: AgentStudioDataSourceLink[];
}

export interface AgentStudioDataSource extends AgentStudioDataSourceRequest {
  id: string;
  state: string;
  lock?: {
    state: string;
    reason?: string;
  };
}

export interface ConnectorOption {
  type: string;
  name: string;
  description: string;
  iconClass: string;
  disabled?: boolean;
}

// Interface for the data structure emitted by lw-template's dataProcessed event
export interface LwTemplateData {
  template: {
    config: Record<string, any>;
  };
  components: Array<{
    type: string; // e.g., 'QA_PAIR', 'RESULTS_LIST_SEARCH'
    id: string;
    config?: {
      cardMappings?: {
        fallback: {
          type: string;
        };
      };
    };
    result?: {
      pairs?: Array<{
        type: string; // e.g., 'QA_PAIR'
        cardMappingKey: string;
        question: string;
        answer: string;
      }>;
    };
  }>;
}

// Interface for the lw-template DOM element with proper typing for its 'data' property.
export interface LwTemplateElement extends HTMLElement {
  data: {
    id: string;
    template: { config: Record<string, any> };
    components: any[];
  };
}

export interface Extraction {
  [key: string]: ExtractionFieldConfig;
}

export interface ExtractionFieldConfig {
  'query-params'?: string;
  selector?: string;
}

export enum ComponentMarkupTabType {
  MARKUP_ATTRIBUTE = 'Markup Attribute',
  AUTOMATIC_EXTRACTION = 'Automatic Extraction'
}